import './DetailsPreview.scss';
import { prefixClass } from 'lib/utils';
import { RequestType } from '../../../../../../../../Common';
import classNames from 'classnames';
import { format } from 'date-fns';
import { IconChevronDown } from 'components/Icons';
import { formatRangeValues } from 'lib/reportUtils';
import { Tooltip, Whisper } from 'rsuite';

import React, { FC, useMemo, useState } from 'react';

import type {
  MessageDetailsContent,
  MessageDetailsRow,
  MessageDetailsSection,
  MessageDetailsLink,
} from '../../../../analyzeMessageContent';
import type { AskRequest } from '@hone-automation/common';

export interface DetailsPreviewProps {
  content: MessageDetailsContent;
  request?: AskRequest;
}

interface LinkRowProps {
  label: string;
  value: string | number;
  money?: boolean;
}

const formatMoney = (value: string | number): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return String(value);

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(numValue));

  return numValue < 0 ? `($${formatted})` : `$${formatted}`;
};

/**
 * Render row value with proper formatting
 * Matches InfoPanel behavior: shows "N/A" for null/undefined values
 * When originalMoney is provided, renders both values inline with tooltips
 */
const renderRowValue = ({ value, money, originalMoney }: MessageDetailsRow): React.ReactNode => {
  // Handle null/undefined/empty values - show "N/A" like InfoPanel does
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  // Format money values with optional original amount
  if (money && value !== '-') {
    const formattedCurrent = formatMoney(value);

    // If we have an original amount that differs, show both with tooltips
    if (originalMoney !== undefined && originalMoney !== null && originalMoney !== value) {
      const formattedOriginal = formatMoney(originalMoney);
      return (
        <>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Current Amount</Tooltip>}>
            <span className="current-money">{formattedCurrent}</span>
          </Whisper>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Original Amount</Tooltip>}>
            <span className="original-money">/ {formattedOriginal}</span>
          </Whisper>
        </>
      );
    }

    return formattedCurrent;
  }

  return value;
};

/**
 * LinkRow component for displaying link detail items
 * Matches InfoPanel's InfoDetailsItem behavior
 */
const LinkRow: FC<LinkRowProps> = ({ label, value, money = false }) => {
  const prefix = prefixClass('q2-message-preview-module-details');
  const formattedValue = money && value !== '-' ? formatMoney(value) : value || 'N/A';

  return (
    <div className={prefix('row')}>
      <dt className={prefix('row-label')}>{label}</dt>
      <dd className={classNames(prefix('row-value'), { 'is-money': money })}>{formattedValue}</dd>
    </div>
  );
};

/**
 * Generates the "Commenting on..." summary line as JSX with bold formatting
 * For Flagged Cell requests, sectionPath from links is shown when available
 */
const generateSummaryLine = (
  request: AskRequest,
  sectionPath?: string | null,
  dataDateRange?: MessageDetailsLink['dataDateRange']
): React.ReactNode | null => {
  const { type, flaggedAmount, transaction, identifier, createdAt } = request;

  const formatAmount = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return String(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  const formatDate = (dateValue: string | Date | undefined): string => {
    if (!dateValue) return '';
    try {
      const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
      return format(date, 'M/d/yy');
    } catch {
      return '';
    }
  };

  const amount = formatAmount(flaggedAmount || transaction?.amount);
  // Use transaction date for transaction-based requests, createdAt for others
  const trxDate = formatDate(transaction?.date);
  const createdDate = formatDate(createdAt);
  const glName = transaction?.account?.split(':').pop()?.trim() || 'N/A';
  const rawRange =
    dataDateRange && (dataDateRange.start || dataDateRange.end)
      ? [dataDateRange.start, dataDateRange.end].filter(Boolean).join(' - ')
      : '';
  const formattedRange = rawRange ? formatRangeValues(rawRange, 'KS') : '';

  switch (type) {
    case 'Ask Bookkeeper':
      return (
        <>
          Commenting on a <strong>{amount}</strong> <em>transaction</em> in <strong>{glName}</strong> on{' '}
          <strong>{trxDate || createdDate}</strong>
        </>
      );
    case 'Flagged Cell': {
      // Show identifier (last element of sectionPath)
      const location = identifier || 'N/A';
      return (
        <>
          Commenting on a <em>flagged cell</em> in <strong>{location}</strong> for <strong>{amount}</strong> on{' '}
          {formattedRange ? <strong>{formattedRange}</strong> : null}
        </>
      );
    }
    case 'Ask Customer':
      return (
        <>
          Commenting on a <strong>{amount}</strong> <em>transaction</em> from{' '}
          <strong>{transaction?.name || 'N/A'}</strong> on <strong>{trxDate || createdDate}</strong>
        </>
      );
    default:
      // Custom requests (Customer Request, Flagged Report) - no synthetic message
      return null;
  }
};

const DetailsPreview: FC<DetailsPreviewProps> = ({ content, request }) => {
  const prefix = prefixClass('q2-message-preview-module');
  const { requestType, sections, footnote, links, linksLoading, linksError } = content;
  // Toggle to show/hide links section (hidden for now, details shown in collapsible section)
  const showLinksSection = false;
  const hasLinks = showLinksSection && (linksLoading || (links && links.length > 0));

  // Accordion collapsed state (starts collapsed)
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Extract sectionPath from first link (for Flagged Cell requests)
  const sectionPath = useMemo(() => {
    if (request?.type !== 'Flagged Cell' || !links || links.length === 0) return null;
    return links[0]?.sectionPath || null;
  }, [request?.type, links]);

  // Extract data date range from first link (for Flagged Cell requests)
  const dataDateRange = useMemo(() => {
    if (request?.type !== 'Flagged Cell' || !links || links.length === 0) return undefined;
    return links[0]?.dataDateRange;
  }, [request?.type, links]);

  const formattedDateRange = useMemo(() => {
    if (!dataDateRange) return null;
    const rawRange =
      dataDateRange && (dataDateRange.start || dataDateRange.end)
        ? [dataDateRange.start, dataDateRange.end].filter(Boolean).join(' - ')
        : '';
    return rawRange ? formatRangeValues(rawRange, 'KS') : null;
  }, [dataDateRange]);

  // Generate summary line for the first message
  const summaryLine = useMemo(() => {
    if (!request) return null;
    return generateSummaryLine(request, sectionPath, dataDateRange);
  }, [request, sectionPath, dataDateRange]);

  return (
    <div className={prefix()}>
      <div className={prefix('details')}>
        {/* Summary line - "Commenting on a $X transaction in Y on Z" */}
        {summaryLine && (
          <div className={prefix('synthetic-message')}>
            <span className={prefix('summary-line')}>{summaryLine}</span>
          </div>
        )}
        <div
          className={classNames(prefix('details-accordion'), { collapsed: isCollapsed })}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <div className={prefix('details-accordion-base')}>
            <div className={prefix('details-accordion-icon')}>
              <IconChevronDown />
            </div>
            <div className={prefix('details-accordion-label')}>Details</div>
          </div>
        </div>
        {!isCollapsed && (
          <div className={prefix('details-body')}>
            {/* Request Details Sections */}
            {sections.map((section: MessageDetailsSection, sectionIndex: number) => (
              <div key={`details-section-${sectionIndex}`} className={prefix('details-section')}>
                {section.heading && <div className={prefix('details-section-heading')}>{section.heading}</div>}
                <dl className={prefix('details-section-list')}>
                  {section.rows.map((row: MessageDetailsRow, rowIndex: number) => (
                    <React.Fragment key={`details-row-${sectionIndex}-${rowIndex}`}>
                      <div
                        className={classNames(prefix('details-row'), {
                          [prefix('details-row--highlight')]: row.highlight,
                        })}
                      >
                        <dt className={prefix('details-row-label')}>{row.label}</dt>
                        <dd className={classNames(prefix('details-row-value'), { 'is-money': row.money })}>
                          {renderRowValue(row)}
                        </dd>
                      </div>

                      {/* Insert "Section" as the 3rd row for Ask Bookkeeper (after index 1) */}
                      {request?.type === 'Ask Bookkeeper' &&
                        links &&
                        links.length > 0 &&
                        links[0].sectionPath &&
                        rowIndex === 1 && (
                          <div
                            key={`details-row-section-${sectionIndex}-${rowIndex}`}
                            className={prefix('details-row')}
                          >
                            <dt className={prefix('details-row-label')}>Section</dt>
                            <dd className={prefix('details-row-value')}>{links[0].sectionPath}</dd>
                          </div>
                        )}
                    </React.Fragment>
                  ))}
                  {/* Add Report and Section rows for Flagged Cell when links are loaded */}
                  {request?.type === 'Flagged Cell' && links && links.length > 0 && links[0].reportTitle && (
                    <div className={prefix('details-row')}>
                      <dt className={prefix('details-row-label')}>Report</dt>
                      <dd className={prefix('details-row-value')}>{links[0].reportTitle}</dd>
                    </div>
                  )}
                  {request?.type === 'Flagged Cell' && links && links.length > 0 && links[0].sectionPath && (
                    <div className={prefix('details-row')}>
                      <dt className={prefix('details-row-label')}>Section</dt>
                      <dd className={prefix('details-row-value')}>{links[0].sectionPath}</dd>
                    </div>
                  )}
                  {request?.type === 'Flagged Cell' && formattedDateRange && (
                    <div className={prefix('details-row')}>
                      <dt className={prefix('details-row-label')}>Dates</dt>
                      <dd className={prefix('details-row-value')}>{formattedDateRange}</dd>
                    </div>
                  )}
                </dl>
              </div>
            ))}

            {/* Links Section - hidden for now, can be re-enabled by setting showLinksSection to true */}
            {hasLinks && (
              <div className={prefix('details-section')}>
                <div className={prefix('details-section-heading')}>Report Info</div>
                <div className={prefix('details-links-container')}>
                  {linksLoading && <div className={prefix('details-links-loading')}>Loading links...</div>}
                  {linksError && <div className={prefix('details-links-error')}>Error loading links</div>}
                  {links && links.length > 0 && (
                    <>
                      {links.map((link: MessageDetailsLink, linkIndex: number) => {
                        const isValid = link.valid !== false;
                        return (
                          <div
                            key={`link-${linkIndex}`}
                            className={classNames(prefix('details-link-item'), {
                              [prefix('details-link-item--invalid')]: !isValid,
                            })}
                          >
                            {!isValid && (
                              <div className={prefix('details-link-invalid-notice')}>
                                Link is no longer valid or has been updated
                              </div>
                            )}

                            <dl className={prefix('details-section-list')}>
                              {link.reportTitle && <LinkRow label="Type" value={link.reportTitle} />}

                              {link.sectionPath && <LinkRow label="Section" value={link.sectionPath} />}

                              {link.sectionAmount !== undefined && link.sectionAmount !== null && (
                                <LinkRow label="Section Total" value={link.sectionAmount} money />
                              )}

                              {link.dataDateRange && (
                                <LinkRow
                                  label="Date Range"
                                  value={`${link.dataDateRange.start || 'N/A'} / ${link.dataDateRange.end || 'N/A'}`}
                                />
                              )}

                              {link.attachments && link.attachments.length > 0 && (
                                <LinkRow
                                  label="Attachments"
                                  value={`${link.attachments.length} file${link.attachments.length > 1 ? 's' : ''}`}
                                />
                              )}
                            </dl>
                          </div>
                        );
                      })}
                    </>
                  )}
                  {!linksLoading && (!links || links.length === 0) && (
                    <div className={prefix('details-links-empty')}>No related links found</div>
                  )}
                </div>
              </div>
            )}

            {footnote && <div className={prefix('footnote')}>{footnote}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailsPreview;
